import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Lock, User, ArrowRight } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { message } from "antd";
import { login, useAuthContext } from "../../auth";
import { useRedirectPath } from "../../utils/hooks";
import { AltoTechLogo } from "../../components/icons/AltoTechLogo";
import axios from "axios";

interface FormData {
  email: string;
  password: string;
}

export function Login() {
  const navigate = useNavigate();
  const { register, handleSubmit } = useForm<FormData>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const redirectPath = useRedirectPath("/map");
  const { isLoggedIn } = useAuthContext();

  if (isLoggedIn) {
    return <Navigate to={redirectPath} replace />;
  }

  const onSubmit = handleSubmit(async (data) => {
    setIsLoading(true);
    setError('');
    
    try {
      await login(data.email, data.password);
      
      // Force page reload to ensure auth state is updated
      setTimeout(() => {
        window.location.href = redirectPath;
      }, 100);
      
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError('Invalid username or password. Please try again.');
      } else {
        setError('An error occurred. Please try again.');
      }
      setIsLoading(false);
    }
  });

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-800 to-slate-900 p-12 flex-col justify-between relative overflow-hidden">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-lg flex items-center justify-center p-2">
              <AltoTechLogo size={48} />
            </div>
          </div>
          
          <h1 className="heading-1 text-white mb-6 !text-5xl !leading-tight">
            Alto CERO<br />Automation<br />Platform
          </h1>
          
          <p className="paragraph-18 text-slate-300">
            Intelligent building automation with real-time monitoring, advanced controls, and energy optimization for sustainable operations.
          </p>
        </div>

        {/* Features */}
        <div className="relative grid grid-cols-2 gap-4 mb-8">
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-4">
            <svg className="w-6 h-6 text-white mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h3 className="heading-6 text-white mb-1">Real-time Analytics</h3>
            <p className="paragraph-12 text-slate-300">Live monitoring with instant insights and alerts</p>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-4">
            <svg className="w-6 h-6 text-white mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <h3 className="heading-6 text-white mb-1">Smart Automation</h3>
            <p className="paragraph-12 text-slate-300">Data-driven controls for optimal energy efficiency</p>
          </div>
        </div>

        {/* Footer */}
        <div className="relative">
          <p className="paragraph-14 text-slate-400">© 2025 Alto CERO. All rights reserved.</p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gradient-to-br from-slate-100 to-slate-200">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">

          {/* Form Header */}
          <div className="text-center mb-8">
            <h2 className="heading-1 text-gray-900 mb-2 text-center">Welcome back</h2>
            <p className="paragraph-16 text-gray-600 text-center">Sign in to continue to your dashboard</p>
          </div>

          {/* Login Form */}
          <form onSubmit={onSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            
            <div>
              <label htmlFor="email" className="heading-6 block text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User size={18} className="text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  {...register("email")}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-gray-900 placeholder-gray-400 bg-white hover:bg-gray-50 focus:bg-white text-base"
                  placeholder="Enter your email"
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="heading-6 block text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock size={18} className="text-gray-400" />
                </div>
                <input
                  id="password"
                  type="password"
                  {...register("password")}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-gray-900 placeholder-gray-400 bg-white hover:bg-gray-50 focus:bg-white text-base"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="paragraph-14 ml-2 text-gray-700">Remember me</span>
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-gray-800 to-gray-900 text-white py-3 rounded-lg font-semibold hover:from-gray-900 hover:to-black focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span className="button-l">Signing in...</span>
                </>
              ) : (
                <>
                  <span className="button-l">Sign in</span>
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}